import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Pencil, Trash2, Plus, Phone, Mail, Cake, UserCog } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useLookups } from "@/lib/useLookups";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QuestionnaireForm, QuestionnaireView } from "@/components/QuestionnaireForm";
import { PageHeader, Panel, GoldButton, GhostButton, Select } from "@/components/common";
import { ClientInfoForm } from "@/pages/NewClient";
import { TreatmentsTab, AppointmentsTab, PurchasesTab } from "@/pages/ClientTabs";
import { QuoteList } from "@/pages/Quotes";
import { LoyaltyCard } from "@/components/LoyaltyCard";

const ClientQuotes = ({ clientId }) => {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/quotes", { params: { client_id: clientId } }).then((r) => setItems(r.data)); }, [clientId]);
  return (
    <div>
      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <GoldButton onClick={() => nav(`/preventivi/nuovo?type=laser&client=${clientId}`)} data-testid="client-new-laser-quote"><Plus className="h-4 w-4" /> Preventivo laser</GoldButton>
        <GhostButton onClick={() => nav(`/preventivi/nuovo?type=viso&client=${clientId}`)} data-testid="client-new-package-quote"><Plus className="h-4 w-4" /> Pacchetto viso/corpo</GhostButton>
      </div>
      {items && <QuoteList items={items} showClient={false} />}
    </div>
  );
};

const Info = ({ icon: Icon, children }) => children ? <div className="flex items-center gap-2 text-sm text-[#6E675F]"><Icon className="h-4 w-4 text-[#C5A059]" strokeWidth={1.5} />{children}</div> : null;

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const lk = useLookups(["clients", "treatments", "operators", "products"]);
  const [c, setC] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => api.get(`/clients/${id}`).then((r) => setC(r.data)).catch(() => setC(false)), [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!edit.f.first_name.trim() || !edit.f.last_name.trim()) return toast.error("Inserisci nome e cognome");
    try {
      const { operator_name, id: _i, created_at, ...body } = edit.f;
      await api.put(`/clients/${id}`, body);
      toast.success("Scheda aggiornata");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Eliminare definitivamente questo cliente, i suoi trattamenti e appuntamenti?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Cliente eliminato");
    nav("/clienti");
  };
  const setOperator = async (e) => {
    const { operator_name, id: _i, created_at, ...body } = c;
    await api.put(`/clients/${id}`, { ...body, operator_id: e.target.value });
    toast.success("Operatore aggiornato");
    load();
  };

  if (c === false) return <div className="text-[#6E675F]">Cliente non trovato. <Link to="/clienti" className="text-[#C5A059]">Torna ai clienti</Link></div>;
  if (!c) return null;

  return (
    <div data-testid="client-detail-page">
      <Link to="/clienti" className="mb-4 inline-flex items-center gap-1 text-sm text-[#6E675F] hover:text-[#C5A059]" data-testid="back-to-clients"><ArrowLeft className="h-4 w-4" /> Clienti</Link>
      <PageHeader overline="Scheda cliente" title={<span data-testid="client-name">{c.first_name} {c.last_name}</span>}>
        <GhostButton onClick={() => setEdit({ mode: "info", f: { ...c } })} data-testid="edit-client-button"><Pencil className="h-4 w-4" /> Modifica</GhostButton>
        <GhostButton onClick={remove} className="text-red-700" data-testid="delete-client-button"><Trash2 className="h-4 w-4" /></GhostButton>
      </PageHeader>

      <Panel className="mb-8 p-6 grid gap-5 md:grid-cols-4 items-center">
        <Info icon={Phone}>{c.phone}</Info>
        <Info icon={Mail}>{c.email}</Info>
        <Info icon={Cake}>{c.birth_date && fmtDate(c.birth_date)}</Info>
        <div className="flex items-center gap-2">
          <UserCog className="h-4 w-4 text-[#C5A059] shrink-0" strokeWidth={1.5} />
          <Select value={c.operator_id || ""} onChange={setOperator} placeholder="Operatore di riferimento" options={lk.operators.map((o) => ({ value: o.id, label: o.name }))} className="py-1.5" data-testid="client-detail-operator-select" />
        </div>
        {c.notes && <p className="md:col-span-4 text-sm text-[#6E675F] border-t border-[#E8E1D7] pt-4">{c.notes}</p>}
      </Panel>

      <Tabs defaultValue="trattamenti">
        <TabsList className="mb-6 h-auto flex-wrap bg-[#F6F1EA] p-1 rounded-full">
          {[["trattamenti", "Trattamenti"], ["appuntamenti", "Appuntamenti"], ["acquisti", "Prodotti acquistati"], ["preventivi", "Preventivi"], ["fedelta", "Tessera fedeltà"], ["scheda", "Scheda esigenze"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-full px-4 data-[state=active]:bg-[#121212] data-[state=active]:text-[#F6F1EA]">{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="trattamenti"><TreatmentsTab clientId={id} lk={lk} /></TabsContent>
        <TabsContent value="appuntamenti"><AppointmentsTab clientId={id} lk={lk} /></TabsContent>
        <TabsContent value="acquisti"><PurchasesTab clientId={id} lk={lk} /></TabsContent>
        <TabsContent value="preventivi"><ClientQuotes clientId={id} /></TabsContent>
        <TabsContent value="fedelta"><LoyaltyCard clientId={id} clientName={`${c.first_name} ${c.last_name}`} /></TabsContent>
        <TabsContent value="scheda">
          <Panel className="p-6 sm:p-8">
            <div className="mb-6 flex justify-end">
              <GhostButton onClick={() => setEdit({ mode: "q", f: { ...c } })} data-testid="edit-questionnaire-button"><Pencil className="h-4 w-4" /> Modifica scheda</GhostButton>
            </div>
            <QuestionnaireView value={c.questionnaire} />
          </Panel>
        </TabsContent>
      </Tabs>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-[#FDFBF7] border-[#E8E1D7] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-client-dialog">
          <DialogHeader><DialogTitle className="font-serif text-3xl font-normal">{edit?.mode === "q" ? "Scheda esigenze" : "Dati anagrafici"}</DialogTitle></DialogHeader>
          {edit?.mode === "info" && <ClientInfoForm f={edit.f} setF={(f) => setEdit({ ...edit, f })} operators={lk.operators} />}
          {edit?.mode === "q" && <QuestionnaireForm value={edit.f.questionnaire || {}} onChange={(q) => setEdit({ ...edit, f: { ...edit.f, questionnaire: q } })} />}
          <div className="flex justify-end"><GoldButton onClick={save} data-testid="save-client-button"><Plus className="h-4 w-4 hidden" />Salva</GoldButton></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
